import React from 'react';
import { Link } from 'react-router-dom';
import { PageTransition } from '@/components/motion';

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-bold text-foreground mb-4">{title}</h2>
      <div className="text-muted-foreground text-sm leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Background decoration */}
      <div className="fixed inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-neon/5 pointer-events-none" />

      {/* Navbar */}
      <header className="relative z-10 flex items-center justify-between px-6 h-16 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2 group">
          <div className="w-7 h-7 text-neon group-hover:text-green-400 transition-colors">
            <EyeIcon />
          </div>
          <span className="text-foreground font-bold text-lg">Proton</span>
          <span className="text-neon text-xs font-semibold uppercase tracking-widest ml-1 hidden sm:block">Trading</span>
        </Link>
        <Link to="/" className="text-neon hover:text-green-400 font-semibold text-sm transition-colors">
          Back to Home
        </Link>
      </header>

      {/* Content */}
      <main className="relative z-10 flex-1 px-4 py-12">
        <PageTransition className="w-full max-w-3xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-extrabold text-foreground mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground text-sm">
              Your privacy matters. Here is how Proton Trading handles your data.
            </p>
          </div>

          {/* Policy Card */}
          <div className="rounded-2xl border border-white/8 bg-card/60 backdrop-blur-sm p-8 sm:p-10">
            {/* Last Updated */}
            <p className="text-xs text-muted-foreground mb-10 border-b border-border pb-4">
              <span className="font-semibold text-foreground">Last Updated:</span> March 7, 2026
            </p>

            <p className="text-sm text-muted-foreground leading-relaxed mb-10">
              This Privacy Policy describes how Proton Trading ("we", "us", or "our") collects, uses, stores, and
              protects your personal information when you use our automated trading bot platform and related
              services (collectively, the "Platform"). By accessing or using the Platform, you agree to the
              practices described in this policy.
            </p>

            {/* 1. Information We Collect */}
            <Section title="1. Information We Collect">
              <p>We collect the following categories of information when you register for and use the Platform:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>
                  <span className="text-foreground font-medium">Account Information</span> — Your name, email
                  address, and a securely hashed version of your password. We never store your password in plain
                  text.
                </li>
                <li>
                  <span className="text-foreground font-medium">Trading Activity Data</span> — Information
                  about your trading strategies, executed trades, open and closed positions, order history, and
                  performance metrics.
                </li>
                <li>
                  <span className="text-foreground font-medium">Broker Connection Data</span> — Encrypted API
                  keys, broker account identifiers, and connection status information required to link your
                  brokerage accounts to the Platform.
                </li>
                <li>
                  <span className="text-foreground font-medium">Usage Data</span> — Pages visited, features
                  used, interaction patterns, timestamps, and session duration to help us understand how the
                  Platform is used and improve the experience.
                </li>
                <li>
                  <span className="text-foreground font-medium">Device and Browser Information</span> — IP
                  address, browser type and version, operating system, device type, screen resolution, and
                  referring URLs collected automatically when you access the Platform.
                </li>
              </ul>
            </Section>

            {/* 2. How We Use Information */}
            <Section title="2. How We Use Information">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Provide, operate, and maintain the Platform and its core trading services.</li>
                <li>Execute trades and manage strategies on your behalf through connected broker accounts.</li>
                <li>Improve, personalize, and optimize the Platform experience based on usage patterns.</li>
                <li>Send transactional notifications such as trade confirmations, strategy alerts, and account
                  security updates.</li>
                <li>Communicate important changes to the Platform, including updates to these policies.</li>
                <li>Detect, prevent, and respond to fraud, unauthorized access, and other security threats.</li>
                <li>Comply with applicable legal obligations and regulatory requirements.</li>
              </ul>
            </Section>

            {/* 3. Data Storage and Security */}
            <Section title="3. Data Storage and Security">
              <p>
                We take the security of your data seriously and implement industry-standard measures to protect
                it:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>All data is transmitted using TLS/SSL encryption to prevent interception in transit.</li>
                <li>Your data is stored on secure, access-controlled servers with regular security audits.</li>
                <li>Broker API keys are encrypted at rest using AES-256 encryption and are never stored in
                  plain text. Decryption occurs only at the moment of trade execution within an isolated
                  environment.</li>
                <li>Passwords are hashed using industry-standard algorithms (bcrypt) and are never reversible.</li>
                <li>Access to production systems is restricted to authorized personnel and protected by
                  multi-factor authentication.</li>
              </ul>
              <p>
                While we strive to protect your personal information, no method of electronic storage or
                transmission is 100% secure. We cannot guarantee absolute security but are committed to
                promptly addressing any security incidents.
              </p>
            </Section>

            {/* 4. Third-Party Broker Data */}
            <Section title="4. Third-Party Broker Data">
              <p>
                When you connect a brokerage account to the Platform, certain data is shared with your
                connected broker(s) to facilitate trade execution. This includes order details, position sizes,
                and strategy parameters necessary to place and manage trades on your behalf.
              </p>
              <p>
                We only transmit the minimum data required for trade execution. We do not sell or share your
                broker connection data with any third parties beyond the brokers you have explicitly authorized.
              </p>
              <p>
                Each broker has its own privacy policy and data handling practices. We encourage you to review
                the privacy policy of any broker you connect to the Platform, as their handling of your data is
                governed by their own terms and not by this policy.
              </p>
            </Section>

            {/* 5. Cookies and Tracking */}
            <Section title="5. Cookies and Tracking">
              <p>We use cookies and similar technologies for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>
                  <span className="text-foreground font-medium">Session Cookies</span> — Essential cookies
                  used for authentication, maintaining your logged-in session, and ensuring the security of
                  your account. These are strictly necessary for the Platform to function.
                </li>
                <li>
                  <span className="text-foreground font-medium">Analytics</span> — We may use analytics tools
                  to collect anonymized usage data that helps us understand how users interact with the
                  Platform, identify performance issues, and prioritize feature development.
                </li>
              </ul>
              <p>
                We do not use cookies for advertising or third-party tracking purposes. You can manage cookie
                preferences through your browser settings, although disabling essential cookies may prevent
                you from using certain features of the Platform.
              </p>
            </Section>

            {/* 6. Data Retention */}
            <Section title="6. Data Retention">
              <p>
                We retain your personal information and trading data for as long as your account is active or
                as needed to provide you with the Platform services. Specifically:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Account information is retained for the duration of your active account.</li>
                <li>Trading history and strategy data are retained for a minimum of 5 years to support
                  performance analysis, regulatory compliance, and dispute resolution.</li>
                <li>Usage and analytics data is retained in anonymized form for up to 2 years.</li>
              </ul>
              <p>
                Upon account deletion, we will remove or anonymize your personal information within 30 days,
                except where retention is required by law or for legitimate business purposes (such as fraud
                prevention or regulatory compliance). Encrypted broker API keys are permanently deleted
                immediately upon account deletion or broker disconnection.
              </p>
            </Section>

            {/* 7. Your Rights */}
            <Section title="7. Your Rights">
              <p>
                You have the following rights regarding your personal information, subject to applicable law:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>
                  <span className="text-foreground font-medium">Access</span> — Request a copy of the
                  personal data we hold about you.
                </li>
                <li>
                  <span className="text-foreground font-medium">Correction</span> — Request correction of
                  inaccurate or incomplete personal data.
                </li>
                <li>
                  <span className="text-foreground font-medium">Deletion</span> — Request deletion of your
                  personal data, subject to legal retention obligations.
                </li>
                <li>
                  <span className="text-foreground font-medium">Export</span> — Export your complete trading
                  history, strategy configurations, and account data in a portable, machine-readable format.
                </li>
                <li>
                  <span className="text-foreground font-medium">Restriction</span> — Request that we limit
                  the processing of your personal data under certain circumstances.
                </li>
                <li>
                  <span className="text-foreground font-medium">Objection</span> — Object to the processing
                  of your personal data for specific purposes.
                </li>
              </ul>
              <p>
                To exercise any of these rights, please contact us at{' '}
                <a href="mailto:privacy@tradeproton.com" className="text-neon hover:text-green-400 transition-colors">
                  privacy@tradeproton.com
                </a>. We will respond to your request within 30 days.
              </p>
            </Section>

            {/* 8. Children's Privacy */}
            <Section title="8. Children's Privacy">
              <p>
                The Platform is not intended for use by individuals under the age of 18. We do not knowingly
                collect personal information from children under 18. If we become aware that we have
                inadvertently collected personal data from a person under 18, we will take immediate steps to
                delete that information from our systems.
              </p>
              <p>
                If you are a parent or guardian and believe your child has provided us with personal
                information, please contact us at{' '}
                <a href="mailto:privacy@tradeproton.com" className="text-neon hover:text-green-400 transition-colors">
                  privacy@tradeproton.com
                </a>{' '}
                so we can take appropriate action.
              </p>
            </Section>

            {/* 9. International Data Transfers */}
            <Section title="9. International Data Transfers">
              <p>
                Your information may be transferred to and processed in countries other than your country of
                residence. These countries may have data protection laws that differ from the laws of your
                jurisdiction.
              </p>
              <p>
                When we transfer your data internationally, we implement appropriate safeguards to ensure that
                your personal information receives an adequate level of protection, including standard
                contractual clauses, data processing agreements, and compliance with applicable data transfer
                regulations.
              </p>
            </Section>

            {/* 10. Changes to This Policy */}
            <Section title="10. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices,
                technologies, legal requirements, or other factors. When we make material changes, we will:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2">
                <li>Update the "Last Updated" date at the top of this policy.</li>
                <li>Notify you via email or through a prominent notice on the Platform prior to the changes
                  taking effect.</li>
                <li>Where required by law, obtain your consent before applying material changes to how we
                  handle your personal data.</li>
              </ul>
              <p>
                We encourage you to review this Privacy Policy periodically to stay informed about how we
                protect your information.
              </p>
            </Section>

            {/* 11. Contact Information */}
            <Section title="11. Contact Information">
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data
                practices, please contact us:
              </p>
              <div className="mt-4 rounded-xl bg-background/50 border border-border p-5 space-y-2">
                <p>
                  <span className="text-foreground font-medium">Email:</span>{' '}
                  <a href="mailto:privacy@tradeproton.com" className="text-neon hover:text-green-400 transition-colors">
                    privacy@tradeproton.com
                  </a>
                </p>
                <p>
                  <span className="text-foreground font-medium">Subject Line:</span>{' '}
                  Privacy Policy Inquiry
                </p>
              </div>
              <p className="mt-4">
                We aim to respond to all privacy-related inquiries within 30 business days.
              </p>
            </Section>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center space-y-2">
            <p className="text-xs text-slate-600">
              Trading involves substantial risk of loss. Past performance does not guarantee future results.
            </p>
            <p className="text-xs text-muted-foreground">
              &copy; {new Date().getFullYear()} Proton Trading. All rights reserved.
            </p>
          </div>
        </PageTransition>
      </main>
    </div>
  );
}
